import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import {
  Box, Typography, TextField, MenuItem, Button, Paper, Alert, Grid
} from '@mui/material';
import '../css/EditUser.css';
import API_BASE_URL from '../api';

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    gender: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    password: '',
    phoneNumber: '',
    arrive: '',
    userType: 'user',
    societe: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const res = await axios.get(`${API_BASE_URL}/api/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = res.data;
        setFormData({
          firstName: user.firstName,
          lastName: user.lastName,
          emailAddress: user.emailAddress,
          password: '',
          gender: user.gender,
          phoneNumber: user.phoneNumber,
          arrive: user.arrive,
          userType: user.userType,
          societe: user.societe ? user.societe._id : ''
        });
        setIsVerified(user.isVerified);
        setVerificationCode(user.verificationCode);
      } catch (err) {
        setError("Erreur lors de la récupération de l'utilisateur");
      }
    };

    const fetchSocietes = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const res = await axios.get(`${API_BASE_URL}/api/societes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSocietes(res.data);
      } catch (err) {
        console.error('Erreur chargement sociétés');
      }
    };

    fetchUser();
    fetchSocietes();
  }, [id]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`${API_BASE_URL}/api/users/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Utilisateur modifié avec succès');
      setTimeout(() => navigate('/manage-users'), 1000);
    } catch (error) {
      if (error.response?.status === 400 && error.response.data.message === 'User already exists') {
        setError('Un utilisateur avec cet email existe déjà');
      } else {
        setError('Erreur lors de la modification');
      }
      setSuccess('');
    }
  };

  const handleVerify = async () => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`${API_BASE_URL}/api/users/verify/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsVerified(true);
      setSuccess('Compte vérifié avec succès');
      setError('');
    } catch {
      setError('Erreur lors de la vérification');
    }
  };

  return (
    <Box className="edit-user-container">
      <Sidebar />
      <Box className="main-content">
        <Paper className="edit-user-paper" elevation={3}>
          <Typography variant="h5" gutterBottom>Modifier l'utilisateur</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select name="gender" label="Genre" fullWidth required
                  value={formData.gender} onChange={handleChange}
                >
                  <MenuItem value="">Genre</MenuItem>
                  <MenuItem value="Monsieur">Monsieur</MenuItem>
                  <MenuItem value="Madame">Madame</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  name="firstName" label="Prénom" fullWidth required
                  value={formData.firstName} onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  name="lastName" label="Nom" fullWidth required
                  value={formData.lastName} onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="email" name="emailAddress" label="Email" fullWidth required
                  value={formData.emailAddress} onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  type="password" name="password" label="Nouveau mot de passe" fullWidth
                  value={formData.password} onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  name="phoneNumber" label="Téléphone" fullWidth required
                  value={formData.phoneNumber} onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select name="arrive" label="Type" fullWidth required
                  value={formData.arrive} onChange={handleChange}
                >
                  <MenuItem value="ESN">ESN</MenuItem>
                  <MenuItem value="Freelance">Freelance</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select name="userType" label="Rôle" fullWidth required
                  value={formData.userType} onChange={handleChange}
                >
                  <MenuItem value="user">Utilisateur</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="commercialadmin">Commercial Admin</MenuItem>
                </TextField>
              </Grid>

              {formData.arrive === 'ESN' && (
                <Grid item xs={12}>
                  <TextField
                    select name="societe" label="Société" fullWidth required
                    value={formData.societe} onChange={handleChange}
                  >
                    <MenuItem value="">Sélectionner une société</MenuItem>
                    {societes.map(s => (
                      <MenuItem key={s._id} value={s._id}>{s.nom}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button type="submit" variant="contained" className="submit-btn">
                  Modifier l'utilisateur
                </Button>
              </Grid>
            </Grid>
          </form>

          {!isVerified && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">Ce compte n'est pas encore vérifié.</Typography>
              <Button
                onClick={handleVerify}
                variant="outlined"
                className="verify-user-button"
                sx={{ mt: 1 }}
              >
                Vérifier le compte
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default EditUser;
